import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import knowledgeApi from '@/lib/knowledge-api'
import { Database, Loader2, Pencil, Search } from 'lucide-react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { DocumentsTab } from '@/components/knowledge/DocumentsTab'
import { SettingsTab } from '@/components/knowledge/SettingsTab'
import { GenClairTab } from '@/components/knowledge/genclair/GenClairTab'
import { KnowledgeBaseTags } from '@/pages/KnowledgeBaseTags'
import { formatDistanceToNow } from 'date-fns'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useState } from 'react'

export function KnowledgeBaseDetail() {
  const { kbId } = useParams<{ kbId: string }>()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')

  const { data: kb, isLoading } = useQuery({
    queryKey: ['knowledge-base', kbId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/api/kb/${kbId}/`)
      return response.data
    },
    enabled: !!kbId,
  })

  // Fetch tagset data to check for reparse status
  const { data: tagsetsData } = useQuery({
    queryKey: ['tagsets', kbId],
    queryFn: async () => {
      const response = await knowledgeApi.get(`/knowledge/${kbId}/tagsets/`)
      return response.data
    },
    enabled: !!kbId,
  })

  // Reparse mutation
  const reparseAll = useMutation({
    mutationFn: async () => {
      console.log('🔄 Starting reparse for KB:', kbId)
      const response = await knowledgeApi.post(`/knowledge/${kbId}/reparse-all/`)
      console.log('✅ Reparse response:', response.data)
      return response.data
    },
    onSuccess: (data) => {
      console.log('🎉 Reparse success:', data)
      queryClient.invalidateQueries({ queryKey: ['tagsets', kbId] })
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
      toast({
        title: 'Reparsing started',
        description: data.message || 'All documents are being reparsed with updated tags',
      })
    },
    onError: (error: any) => {
      console.error('❌ Reparse error:', error)
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to start reparsing',
        variant: 'destructive',
      })
    },
  })

  const needsReparse = tagsetsData?.kb_needs_reparse || false
  const reparseReason = tagsetsData?.reparse_reason || ''

  // Update mutation
  const updateKB = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      const response = await knowledgeApi.put(`/knowledge/api/kb/${kbId}/update/`, data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', kbId] })
      toast({
        title: 'Success',
        description: 'Knowledge base updated successfully',
      })
      setIsEditDialogOpen(false)
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error || 'Failed to update knowledge base',
        variant: 'destructive',
      })
    },
  })

  const handleEditClick = () => {
    if (kb) {
      setEditName(kb.name)
      setEditDescription(kb.description || '')
      setIsEditDialogOpen(true)
    }
  }

  const handleSaveEdit = () => {
    if (!editName.trim()) {
      toast({
        title: 'Error',
        description: 'Knowledge base name cannot be empty',
        variant: 'destructive',
      })
      return
    }
    updateKB.mutate({
      name: editName.trim(),
      description: editDescription.trim(),
    })
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>
  }

  if (!kb) {
    return <div className="flex items-center justify-center h-64">Knowledge base not found</div>
  }

  return (
    <div className="container mx-auto px-4 py-4">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
            <Database className="w-6 h-6 text-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold">{kb.name}</h1>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleEditClick}
                className="h-8 w-8 p-0"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-muted-foreground mt-1">{kb.description}</p>
            {kb.visibility && (
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border mt-2">
                {kb.visibility}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className="text-sm text-muted-foreground">
            Updated {formatDistanceToNow(new Date(kb.updated_at), { addSuffix: true })}
          </span>
        </div>
      </div>

      {/* Reparse Alert Banner */}
      {needsReparse && (
        <Alert className="mb-4 border-yellow-200 bg-yellow-50 flex items-center">
          
          <AlertDescription className="flex items-center justify-between flex-1">
            <div className="flex-1">
              <span className="font-semibold text-yellow-900">‼️ Reparsing Required:</span>{' '}
              <span className="text-sm text-yellow-800">{reparseReason}</span>
            </div>
            <Button
              size="sm"
              onClick={() => reparseAll.mutate()}
              disabled={reparseAll.isPending}
              className="ml-4"
            >
              {reparseAll.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Reparsing...
                </>
              ) : (
                'Reparse All Documents'
              )}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs defaultValue="documents" className="space-y-4">
        <TabsList>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="tags">Tags</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger
            value="genclair"
            disabled={!kb?.ready_document_count || kb.ready_document_count === 0}
            title={!kb?.ready_document_count || kb.ready_document_count === 0
              ? 'No parsed documents yet'
              : undefined
            }
          >
            <Search className="w-4 h-4 mr-1" />
            GenClair
          </TabsTrigger>
        </TabsList>

        <TabsContent value="documents">
          <DocumentsTab kbId={kbId!} kbStats={kb} />
        </TabsContent>

        <TabsContent value="tags">
          <KnowledgeBaseTags />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab 
            kbId={kbId!} 
            kbName={kb.name}
            needsReparse={kb.needs_reparse || false}
            reparseReason={kb.reparse_reason || ''}
          />
        </TabsContent>


        <TabsContent value="genclair">
          <GenClairTab
            kbId={kbId!}
            parsedCount={kb?.ready_document_count ?? 0}
            totalCount={kb?.document_count ?? 0}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Knowledge Base</DialogTitle>
            <DialogDescription>
              Update the name and description of your knowledge base.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="Knowledge base name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Knowledge base description"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={updateKB.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updateKB.isPending}>
              {updateKB.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
